import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "antd";

export interface DynamicInputProps {
  /** 动态展示的文案数组 */
  texts: string[];
  /** 输入框占位符 */
  placeholder?: string;
  /** 动画速度（毫秒） */
  animationSpeed?: number;
  /** 是否自动播放动画 */
  autoPlay?: boolean;
  /** 输入框值 */
  value?: string;
  /** 输入框变化回调 */
  onChange?: (value: string) => void;
  /** 其他 Input 属性 */
  [key: string]: unknown;
}

type DocgenInfo = {
  displayName: string;
  description: string;
  props: Record<string, {
    name: string;
    required: boolean;
    description: string;
    type: { name: string; raw: string };
    defaultValue?: { value: string };
  }>;
};

type ComponentWithDocgen = React.FC<DynamicInputProps> & { __docgenInfo?: DocgenInfo };

/** 动态文案输入框组件，支持流式文字动画效果 */
export const DynamicInput: React.FC<DynamicInputProps> = ({
  texts = [],
  placeholder = "请输入内容",
  animationSpeed = 100,
  autoPlay = true,
  value,
  onChange,
  ...inputProps
}) => {
  const [currentText, setCurrentText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  
  const animationRef = useRef<number | undefined>(undefined);
  const typingRef = useRef<number | undefined>(undefined);
  const textsRef = useRef(texts);
  const currentIndexRef = useRef(currentIndex);
  const isFocusedRef = useRef(isFocused);
  const currentTextRef = useRef(currentText);

  // 更新 refs
  useEffect(() => {
    textsRef.current = texts;
    currentIndexRef.current = currentIndex;
    isFocusedRef.current = isFocused;
    currentTextRef.current = currentText;
  });

  // 打字机效果
  const typeText = useCallback((text: string, callback?: () => void) => {
    let index = 0;
    setIsTyping(true);
    
    const typeChar = () => {
      if (index < text.length) {
        setCurrentText(text.slice(0, index + 1));
        index++;
        typingRef.current = window.setTimeout(typeChar, animationSpeed);
      } else {
        setIsTyping(false);
        callback?.();
      }
    };
    
    typeChar();
  }, [animationSpeed]);

  // 删除文字效果
  const deleteText = useCallback((callback?: () => void) => {
    let index = currentTextRef.current.length;
    
    const deleteChar = () => {
      if (index > 0) {
        setCurrentText(currentTextRef.current.slice(0, index - 1));
        index--;
        typingRef.current = window.setTimeout(deleteChar, animationSpeed / 2);
      } else {
        callback?.();
      }
    };
    
    deleteChar();
  }, [animationSpeed]);

  // 循环播放动画
  const playAnimation = useCallback(() => {
    if (textsRef.current.length === 0 || isFocusedRef.current) return;
    
    setIsAnimating(true);
    
    const currentTextItem = textsRef.current[currentIndexRef.current];
    
    // 如果当前有文字，先删除
    if (currentTextRef.current) {
      deleteText(() => {
        // 删除完成后，开始输入新文字
        typeText(currentTextItem, () => {
          // 输入完成后，等待一段时间再继续
          animationRef.current = window.setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % textsRef.current.length);
            setIsAnimating(false);
          }, 2000);
        });
      });
    } else {
      // 直接输入新文字
      typeText(currentTextItem, () => {
        animationRef.current = window.setTimeout(() => {
          setCurrentIndex((prev) => (prev + 1) % textsRef.current.length);
          setIsAnimating(false);
        }, 2000);
      });
    }
  }, [deleteText, typeText]);

  // 开始动画
  useEffect(() => {
    if (autoPlay && texts.length > 0 && !isFocused) {
      playAnimation();
    }
    
    return () => {
      if (animationRef.current) {
        window.clearTimeout(animationRef.current);
      }
      if (typingRef.current) {
        window.clearTimeout(typingRef.current);
      }
    };
  }, [currentIndex, isFocused, autoPlay, texts.length, playAnimation]);

  // 当索引变化时重新播放动画
  useEffect(() => {
    if (!isAnimating && autoPlay && texts.length > 0 && !isFocused) {
      playAnimation();
    }
  }, [currentIndex, isAnimating, autoPlay, texts.length, isFocused, playAnimation]);

  // 处理焦点事件
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // 清除所有动画
    if (animationRef.current) {
      window.clearTimeout(animationRef.current);
    }
    if (typingRef.current) {
      window.clearTimeout(typingRef.current);
    }
    setIsAnimating(false);
    setIsTyping(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (inputProps as any).onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // 如果输入框为空，重新开始动画
    if (!value && texts.length > 0) {
      setCurrentText("");
      setCurrentIndex(0);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (inputProps as any).onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (inputProps as any).onChange?.(e);
  };

  // 获取显示的占位符文本
  const getPlaceholderText = () => {
    if (isFocused || value) {
      return placeholder;
    }
    return currentText || placeholder;
  };

  return (
    <div style={{ position: "relative" }}>
      <Input
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(inputProps as any)}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={getPlaceholderText()}
        style={{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(inputProps as any).style,
          position: "relative",
          zIndex: 1,
        }}
      />
      
      {/* 动态文字覆盖层 */}
      {!isFocused && !value && currentText && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            paddingLeft: 11,
            paddingRight: 11,
            color: "#bfbfbf",
            pointerEvents: "none",
            zIndex: 2,
            fontSize: 14,
            fontFamily: "inherit",
          }}
        >
          <span>{currentText}</span>
          {/* 光标效果 */}
          {isTyping && (
            <span
              style={{
                display: "inline-block",
                width: 1,
                height: "1.2em",
                backgroundColor: "#1890ff",
                marginLeft: 2,
                animation: "blink 1s infinite",
              }}
            />
          )}
        </div>
      )}
      
      {/* 添加光标闪烁动画 */}
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
        `}
      </style>
    </div>
  );
};

// 手动添加 docgen 信息
(DynamicInput as ComponentWithDocgen).__docgenInfo = {
  displayName: "DynamicInput",
  description: "动态文案输入框组件，支持流式文字动画效果",
  props: {
    texts: {
      name: "texts",
      required: true,
      description: "动态展示的文案数组",
      type: { name: "string[]", raw: "string[]" }
    },
    placeholder: {
      name: "placeholder",
      required: false,
      description: "输入框占位符",
      type: { name: "string", raw: "string" },
      defaultValue: { value: '"请输入内容"' }
    },
    animationSpeed: {
      name: "animationSpeed",
      required: false,
      description: "动画速度（毫秒）",
      type: { name: "number", raw: "number" },
      defaultValue: { value: "100" }
    },
    autoPlay: {
      name: "autoPlay",
      required: false,
      description: "是否自动播放动画",
      type: { name: "boolean", raw: "boolean" },
      defaultValue: { value: "true" }
    },
    value: {
      name: "value",
      required: false,
      description: "输入框值",
      type: { name: "string", raw: "string" }
    },
    onChange: {
      name: "onChange",
      required: false,
      description: "输入框变化回调",
      type: { name: "function", raw: "(value: string) => void" }
    }
  }
};